import {Inject, NgModule} from '@angular/core';
import {RequestMethod} from '@angular/http';
import {AppConfig, AppDiConfig} from './app.config';
import {AuthenticationModule, AuthenticationService} from '@ngx-ivengi/authentication';
import {CleanzoneModule, NavigationService, NavItem, NavigationType, InsecureRouteService} from '@ngx-ivengi/cleanzone';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {CrudConfig, CrudModule} from '@ngx-ivengi/crud';

@NgModule({
  imports: [
    AuthenticationModule,
    CleanzoneModule,
    TranslateModule.forChild(),
    CrudModule
  ],
  providers: [
    {
      provide: AppConfig,
      useValue: AppDiConfig
    }
  ]
})
export class ConfigModule {
  constructor(
    @Inject(AppConfig) private appConfig,
    private authService: AuthenticationService,
    private navigationService: NavigationService,
    private translate: TranslateService,
    private crudConfig: CrudConfig,
    private insecureRouteService: InsecureRouteService
  ) {
    this.insecureRouteService
      .setLoginBlockTitle('Inloggen klantportaal')
      .setForgotPasswordState('forgotpassword')
      .setShowForgotPassword(true)
      .setLogoSrc('assets/images/logo/logo-brands.png');

    this.crudConfig.setApiUrl(this.appConfig.apiUrl);
    this.setAuthenticationConfig();
    this.setNavigationConfig();
  }

  private setAuthenticationConfig() {
    this.authService.setRedirectState('login');
    this.authService.setUnauthorizedState('unauthorized');
    this.authService.config.setRootState('dashboard');
    this.authService.config.setLoginUrl(`${this.appConfig.apiUrl}/authentication/login`);
    this.authService.config.setLogoutUrl(`${this.appConfig.apiUrl}/authentication/logout`);
    this.authService.config.setLogoutHttpMethod(RequestMethod.Post);
    this.authService.config.setRefreshTokenUrl(`${this.appConfig.apiUrl}/authentication/refresh-token`);
    this.authService.config.setForgotPasswordUrl(`${this.appConfig.apiUrl}/authentication/password/forgot`);
    this.authService.config.setPasswordRestoreUrl(`${this.appConfig.apiUrl}/authentication/password/restore`);
  }

  private setNavigationConfig() {

    this.translate.setDefaultLang('nl-NL');
    this.translate.use('nl-NL');

    // sidebar items
    this.navigationService
      .addItem(new NavItem(
        {
          name: this.translate.get('sidebar.navigation.dashboard'),
          icon: 'dashboard',
          route: 'dashboard',
          type: NavigationType.Sidebar,
          scopes: ['dashboard:EMPLOYEE', 'dashboard:CLIENT']
        }
      ))
      .addItem(new NavItem(
        {
          name: this.translate.get('sidebar.navigation.user'),
          icon: 'user',
          route: 'user',
          type: NavigationType.Sidebar,
          scopes: ['user:create', 'user:read', 'user:update', 'user:delete']
        }
      ))
      .addItem(new NavItem(
        {
          name: this.translate.get('sidebar.navigation.building'),
          icon: 'building',
          route: 'building',
          type: NavigationType.Sidebar,
          scopes: ['building:create', 'building:read', 'building:update', 'building:delete']
        }
      ))
      .addItem(new NavItem(
        {
          name: this.translate.get('sidebar.navigation.customer'),
          icon: 'users',
          route: 'customer',
          type: NavigationType.Sidebar,
          scopes: ['customer:create', 'customer:read', 'customer:update', 'customer:delete']
        }
      ))
      .addItem(new NavItem(
        {
          name: this.translate.get('sidebar.navigation.owner'),
          icon: 'vcard',
          route: 'owner',
          type: NavigationType.Sidebar,
          scopes: ['owner:create', 'owner:read', 'owner:update', 'owner:delete']
        }
      ))
      .addItem(new NavItem(
        {
          name: this.translate.get('sidebar.navigation.assignment.parent'),
          icon: 'tasks',
          route: 'assignment',
          type: NavigationType.Sidebar,
          scopes: ['assignment:create', 'assignment:read', 'assignment:update', 'assignment:delete'],
          children: [
            new NavItem({
              name: this.translate.get('sidebar.navigation.assignment.survey'),
              route: 'assignment.survey',
              scopes: ['assignment:create', 'assignment:read', 'assignment:update', 'assignment:delete']
            }),
            new NavItem({
              name: this.translate.get('sidebar.navigation.assignment.execution'),
              route: 'assignment.execution',
              scopes: ['assignment:create', 'assignment:read', 'assignment:update', 'assignment:delete']
            }),
            new NavItem({
              name: this.translate.get('sidebar.navigation.assignment.maintenance'),
              route: 'assignment.maintenance',
              scopes: ['assignment:create', 'assignment:read', 'assignment:update', 'assignment:delete']
            })
          ]
        }
      ))
      .addItem(new NavItem(
        {
          name: this.translate.get('sidebar.navigation.management.parent'),
          icon: 'cogs',
          route: 'management',
          type: NavigationType.Sidebar,
          scopes: [
            'mangement:create', 'management:read', 'management:update', 'management:delete',
          ],
          children: [
            new NavItem({
              name: this.translate.get('sidebar.navigation.management.product'),
              route: 'management.product',
              scopes: ['mangement:create', 'management:read', 'management:update', 'management:delete']
            }),
            new NavItem({
              name: this.translate.get('sidebar.navigation.management.device'),
              route: 'management.device',
              scopes: ['mangement:create', 'management:read', 'management:update', 'management:delete']
            }),
            new NavItem({
              name: this.translate.get('sidebar.navigation.management.report'),
              route: 'management.report',
              scopes: ['mangement:create', 'management:read', 'management:update', 'management:delete']
            }),
            new NavItem({
              name: this.translate.get('sidebar.navigation.management.price'),
              route: 'management.price',
              scopes: ['mangement:create', 'management:read', 'management:update', 'management:delete']
            })
          ]
        }
      ))
      .addItem(new NavItem(
        {
          name: this.translate.get('sidebar.navigation.archive'),
          icon: 'archive',
          route: 'archive',
          type: NavigationType.Sidebar,
          scopes: ['datagrid:CLIENT', 'datagrid:EMPLOYEE']
        }
        ))
      .addItem(new NavItem(
        {
          name: this.translate.get('sidebar.navigation.calculation'),
          icon: 'calculator',
          route: 'calculation',
          type: NavigationType.Sidebar,
          scopes: ['calculation:read']
        }
      ));

    // profile items
    this.navigationService
      .addItem(new NavItem(
        {
          name: this.translate.get('header.profile.navigation.profile'),
          route: 'profile',
          type: NavigationType.Profile
        }
      ))
      .addItem(new NavItem(
        {
          name: this.translate.get('header.profile.navigation.logout'),
          route: 'logout',
          type: NavigationType.Profile,
          divider: true
        }
      ));
  }

}



// WEBPACK FOOTER //
// /var/jenkins_home/workspace/brands-compart-portal-frontend/checkout/src/app/components/config/config.module.ts